import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * Mini-weergave van de toon (1-6) als een 3|3 gesplitste balk: twee groepen van
 * drie segmenten met een duidelijke splitsing tussen de linkerhelft (toon 1-3) en
 * de rechterhelft (toon 4-6), en het actieve toon-segment in de accentkleur.
 * Optioneel een mini-pijlpunt aan de actieve kant (`showArrow`) die de richting
 * van de transformatiepijl spiegelt. Herbruikt in de PHS-pijl, dialogs en cheat-sheet.
 *
 * Maten zijn instelbaar via CSS-variabelen (`--tone-seg-w`, `--tone-seg-h`,
 * `--tone-seg-gap`, `--tone-seg-split`, `--tone-caret-w`, `--tone-caret-h`).
 *
 * Zie `docs/human-design/phs-rave-psychology-visualisatie.md`.
 */
@Component({
  selector: 'hd-tone-scale',
  templateUrl: './hd-tone-scale.component.html',
  styleUrl: './hd-tone-scale.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdToneScaleComponent {
  /** Toon 1-6. Bepaalt het actieve segment en (1-3 vs 4-6) de helft. */
  readonly tone = input.required<number>();
  /** Accentkleur van het actieve segment + pijlpunt (design = rood, personality = zwart). */
  readonly color = input<string>('currentColor');
  /** Toont een mini-pijlpunt aan de actieve kant (richting van de transformatiepijl). */
  readonly showArrow = input(false);

  /** Toon 4-6 → rechterhelft actief → pijl naar rechts. */
  protected readonly pointsRight = computed(() => this.tone() >= 4);

  /** Twee groepen van drie segmenten (de 3|3-splitsing). */
  protected readonly groups = computed(() => {
    const active = this.tone();
    const makeGroup = (start: number) =>
      Array.from({ length: 3 }, (_, i) => {
        const index = start + i;
        return { index, active: index === active - 1 };
      });
    return [makeGroup(0), makeGroup(3)];
  });

  protected readonly ariaLabel = computed(() => `Toon ${this.tone()} van 6`);
}
